/**
 * @fileoverview Table header with click-to-sort title and filter dropdown menu.
 * @module shared/components/ui/TableHeaderMenu
 */

import { ReactElement, useState, useEffect, useRef } from 'react'
import { cn } from '@/shared/utils/cn'
import { Badge } from '@/shared/components/ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Slider } from '@/components/ui/slider'
import { ArrowUp, ArrowDown, ArrowUpDown, Filter } from 'lucide-react'
import { formatInt } from '@/shared/utils/metricsUtils'

export interface TableHeaderMenuProps {
  /** Column title/label */
  title: React.ReactNode
  /** Second line under the title (e.g. the column's live grand total) */
  subtitle?: React.ReactNode
  /** Callback for ascending sort */
  onSortAsc: () => void
  /** Callback for descending sort */
  onSortDesc: () => void
  /** Current sort direction for this column */
  sortDirection: 'asc' | 'desc' | null
  /** Callback when filter is applied */
  onFilter: (value: number, direction: 'lt' | 'gt') => void
  /** Callback to clear filter */
  onClearFilter: () => void
  /** Current active filter */
  currentFilter: { value: number; direction: 'lt' | 'gt' } | undefined
  /** Maximum value for filter slider */
  maxValue: number
  /** Whether to show numeric filter controls */
  isNumeric: boolean
  /** Additional CSS classes */
  className?: string | undefined
  /** Whether dropdown is open */
  open: boolean
  /** Callback when dropdown open state changes */
  onOpenChange: (open: boolean) => void
  /** Content alignment */
  align?: 'start' | 'end' | 'center' | undefined
  /** Custom value formatter for filter display */
  formatValue?: ((value: number) => React.ReactNode) | undefined
}

/**
 * Table header with click-to-sort title and a filter dropdown menu.
 *
 * The title block (label + total + sort arrow) is one button that toggles
 * sort direction (desc first). The funnel trigger opens a filter-only
 * dropdown and only renders for numeric columns.
 *
 * @example
 * ```tsx
 * <TableHeaderMenu
 *   title="Sales"
 *   sortDirection="desc"
 *   onSortAsc={() => setSortConfig({ key: 'sales', direction: 'asc' })}
 *   onSortDesc={() => setSortConfig({ key: 'sales', direction: 'desc' })}
 *   onFilter={(val, dir) => setFilter({ value: val, direction: dir })}
 *   onClearFilter={() => clearFilter('sales')}
 *   currentFilter={filters.sales}
 *   maxValue={10000}
 *   isNumeric={true}
 *   open={openMenuId === 'sales'}
 *   onOpenChange={(open) => setOpenMenuId(open ? 'sales' : null)}
 *   align="end"
 * />
 * ```
 */
export function TableHeaderMenu({
  title,
  subtitle,
  onSortAsc,
  onSortDesc,
  sortDirection,
  onFilter,
  onClearFilter: _onClearFilter,
  currentFilter,
  maxValue,
  isNumeric,
  className,
  open,
  onOpenChange,
  align = 'start',
  formatValue = formatInt,
}: TableHeaderMenuProps): ReactElement {
  // Note: onClearFilter is intentionally unused here as the clear button
  // is rendered in DraggableTableHead for better UX
  void _onClearFilter

  // Pointer-down origin of the sort button — lets us drop the click the
  // browser still fires after a short column drag (the title doubles as a
  // drag handle, so pointer-down must reach the dnd-kit activator).
  const sortPointerOrigin = useRef<{ x: number; y: number } | null>(null)

  // Initialize state from currentFilter
  const [tempFilter, setTempFilter] = useState(currentFilter?.value ?? maxValue)
  const [direction, setDirection] = useState<'lt' | 'gt'>(currentFilter?.direction ?? 'lt')

  // Click-to-edit state for the value badge — lets users type an exact
  // threshold instead of approximating it with the slider.
  const [isEditingValue, setIsEditingValue] = useState(false)
  const [valueDraft, setValueDraft] = useState('')

  const startEditingValue = (): void => {
    // Edit the raw number, not the display format (e.g. 2000, not "2.0K zł").
    setValueDraft(String(Math.round(tempFilter * 100) / 100))
    setIsEditingValue(true)
  }

  const commitValueDraft = (): void => {
    setIsEditingValue(false)
    const parsed = Number(valueDraft.trim().replace(',', '.'))
    if (!Number.isFinite(parsed) || parsed < 0) return
    setTempFilter(parsed)
    onFilter(parsed, direction)
  }

  // Sync state when menu opens (async via requestAnimationFrame to avoid synchronous setState in effect)
  useEffect(() => {
    if (open) {
      // Menu opened - sync state from currentFilter asynchronously
      const rafId = requestAnimationFrame(() => {
        setTempFilter(currentFilter?.value ?? maxValue)
        setDirection(currentFilter?.direction ?? 'lt')
        setIsEditingValue(false)
      })
      return () => cancelAnimationFrame(rafId)
    }
  }, [open, currentFilter, maxValue])

  const justifyClass = {
    start: 'justify-start',
    end: 'justify-end',
    center: 'justify-center',
  }[align]
  const itemsClass = {
    start: 'items-start',
    end: 'items-end',
    center: 'items-center',
  }[align]

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      {/* Label and total live in one column block so both lines share the same
          edge; the sort arrow and filter trigger sit beside the block
          (spanning both lines) instead of inside the title row, where their
          reserved width would push the title out of line with the total. For
          right-aligned columns the controls flip to the LEFT of the block
          (via row-reverse) so the text edge stays flush with the body
          values below. */}
      <div
        className={cn(
          'flex items-center gap-1 w-full h-full select-none outline-none group/header',
          align === 'end' ? 'flex-row-reverse' : justifyClass,
          className
        )}
      >
        <button
          type="button"
          aria-label={sortDirection === 'desc' ? 'Sort ascending' : 'Sort descending'}
          data-testid="header-sort-toggle"
          // Unlike the filter trigger, pointer-down is NOT swallowed here —
          // the title area stays usable as the column drag handle. Plain
          // clicks pass through the PointerSensor's 8px activation distance;
          // the origin check in onClick drops the click the browser still
          // fires after a short drag.
          onPointerDown={(e) => {
            sortPointerOrigin.current = { x: e.clientX, y: e.clientY }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
          }}
          onClick={(e) => {
            const origin = sortPointerOrigin.current
            // e.detail === 0 means a keyboard "click" — no movement to check.
            if (
              e.detail > 0 &&
              origin &&
              Math.hypot(e.clientX - origin.x, e.clientY - origin.y) > 8
            ) {
              return
            }
            if (sortDirection === 'desc') onSortAsc()
            else onSortDesc()
          }}
          className={cn(
            'flex items-center gap-1 min-w-0 cursor-pointer rounded p-0.5 -m-0.5 hover:bg-foreground/10',
            align === 'end' && 'flex-row-reverse'
          )}
        >
          <div className={cn('flex flex-col justify-center min-w-0', itemsClass)}>
            <span className="max-w-full truncate">{title}</span>
            {subtitle != null && (
              <div className="font-mono text-xs font-normal tabular-nums text-muted-foreground/60">
                {subtitle}
              </div>
            )}
          </div>
          {sortDirection === 'asc' && <ArrowUp className="h-3.5 w-3.5 text-foreground shrink-0" />}
          {sortDirection === 'desc' && (
            <ArrowDown className="h-3.5 w-3.5 text-foreground shrink-0" />
          )}
          {!sortDirection && (
            <ArrowUpDown className="h-3.5 w-3.5 opacity-0 group-hover/header:opacity-50 transition-opacity shrink-0" />
          )}
        </button>
        {isNumeric && (
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Column filter"
              data-testid="header-menu-trigger"
              // The whole header cell is the dnd-kit drag activator — swallow
              // pointer-down and Enter/Space here so opening the menu never
              // doubles as a drag start.
              onPointerDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
              }}
              className={cn(
                'ml-0.5 shrink-0 cursor-pointer rounded p-1.5 text-muted-foreground/60 transition-opacity',
                'hover:bg-foreground/10 hover:text-foreground',
                'lg:opacity-0 lg:group-hover/header:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100'
              )}
            >
              <Filter className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
        )}
      </div>
      <DropdownMenuContent
        align="start"
        className="w-64 p-1"
        onCloseAutoFocus={(e: Event) => e.preventDefault()}
        // While typing a value, Escape should only cancel the edit — not
        // dismiss the whole popover.
        onEscapeKeyDown={(e) => {
          if (isEditingValue) {
            e.preventDefault()
            setIsEditingValue(false)
          }
        }}
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        // NOTE: do NOT stopPropagation on pointer-down here. Radix's
        // outside-click dismissal needs the event to reach its document
        // listener, or the next outside click is swallowed (popover would
        // take two clicks to dismiss). DraggableTableHead filters portaled
        // pointer-downs out of the drag activator instead.
      >
        <div className="p-2 pb-4 space-y-3">
          {/* The filter reads as a sentence — "Sales more than 3 000 zł" —
              with the comparison word doubling as the direction toggle. */}
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
            <span className="min-w-0 truncate">{title}</span>
            <button
              type="button"
              data-testid="filter-direction-toggle"
              onClick={() => {
                const newDir = direction === 'gt' ? 'lt' : 'gt'
                setDirection(newDir)

                let newValue = tempFilter
                if (newDir === 'gt' && tempFilter === maxValue) newValue = 0
                else if (newDir === 'lt' && tempFilter === 0) newValue = maxValue

                setTempFilter(newValue)
                onFilter(newValue, newDir)
              }}
              className="cursor-pointer font-bold text-foreground underline decoration-dotted underline-offset-4 hover:decoration-solid"
            >
              {direction === 'gt' ? 'more' : 'less'}
            </button>
            <span>than</span>
            {isEditingValue ? (
              <input
                autoFocus
                type="text"
                inputMode="decimal"
                data-testid="filter-value-input"
                value={valueDraft}
                onChange={(e) => setValueDraft(e.target.value)}
                onFocus={(e) => e.target.select()}
                onBlur={commitValueDraft}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitValueDraft()
                  else if (e.key === 'Escape') setIsEditingValue(false)
                }}
                className="h-6 w-24 min-w-0 rounded-md bg-secondary px-2 font-mono text-sm text-secondary-foreground outline-none ring-1 ring-ring"
              />
            ) : (
              <Badge variant="secondary" asChild>
                <button
                  type="button"
                  data-testid="filter-value-display"
                  title="Click to type an exact value"
                  onClick={startEditingValue}
                  className="cursor-pointer font-mono text-sm hover:bg-secondary/80"
                >
                  {formatValue(tempFilter)}
                </button>
              </Badge>
            )}
          </div>

          <Slider
            value={[tempFilter]}
            max={maxValue}
            step={maxValue / 100}
            onValueChange={(val) => {
              const newVal = val[0] ?? 0
              setTempFilter(newVal)
            }}
            onValueCommit={(val) => {
              const newVal = val[0] ?? 0
              setTempFilter(newVal)
              onFilter(newVal, direction)
            }}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
